import { Fragment, type ReactNode } from "react";
import type { OrgTag } from "@services/api/tags";

export const NO_CATEGORY_LABEL = "Sans catégorie";

/** Catégorie normalisée d'un tag ("" / espaces / absente → null). */
export function tagCategory(tag: Pick<OrgTag, "category">): string | null {
  const c = (tag.category || "").trim();
  return c || null;
}

/** Catégories existantes (distinctes, triées), éventuellement limitées à un scope. */
export function listTagCategories(tags: OrgTag[], scope?: string): string[] {
  const seen = new Map<string, string>();
  for (const t of tags) {
    if (scope && t.scope !== scope) continue;
    const c = tagCategory(t);
    if (c && !seen.has(c.toLowerCase())) seen.set(c.toLowerCase(), c);
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, "fr"));
}

export interface TagGroup {
  category: string | null;
  tags: OrgTag[];
}

/**
 * Regroupe des tags par catégorie (ordre alphabétique, « Sans catégorie » en dernier).
 * L'ordre des tags à l'intérieur d'un groupe est conservé.
 */
export function groupTagsByCategory(tags: OrgTag[]): TagGroup[] {
  const groups = new Map<string, TagGroup>();
  for (const t of tags) {
    const c = tagCategory(t);
    const key = c ? c.toLowerCase() : "";
    if (!groups.has(key)) groups.set(key, { category: c, tags: [] });
    groups.get(key)!.tags.push(t);
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (!a.category) return 1;
    if (!b.category) return -1;
    return a.category.localeCompare(b.category, "fr");
  });
}

/**
 * Pastilles de tags groupées sous des en-têtes de catégorie — à placer DANS le
 * conteneur `flex flex-row flex-wrap` existant. Sans aucune catégorie, rend les
 * pastilles telles quelles (affichage inchangé).
 */
export default function TagCategoryGroups({
  tags,
  renderTag,
}: {
  tags: OrgTag[];
  renderTag: (tag: OrgTag) => ReactNode;
}) {
  const groups = groupTagsByCategory(tags);
  if (!groups.some((g) => g.category)) {
    return <>{tags.map((t) => <Fragment key={t.id}>{renderTag(t)}</Fragment>)}</>;
  }
  return (
    <>
      {groups.map((g) => (
        <div key={g.category ?? "__none__"} className="w-full flex flex-col gap-1">
          <span className="text-h5 font-semibold uppercase tracking-wide text-neutral-400">
            {g.category ?? NO_CATEGORY_LABEL}
          </span>
          <div className="flex flex-row flex-wrap gap-1.5">
            {g.tags.map((t) => <Fragment key={t.id}>{renderTag(t)}</Fragment>)}
          </div>
        </div>
      ))}
    </>
  );
}
